/**
 * src/lib/hooks/useChatStream.js — Shared SSE chat streaming hook.
 *
 * Reusable SSE streaming hook for chat UIs.
 *
 * Owns: messages, input, loading, sessionId, error state.
 * Provides: sendMessage, stopGeneration, clearMessages, editLastMessage.
 *
 * Phase 82-01: Chat system unification.
 */

import { useState, useCallback, useRef } from 'react';
import { getAuthHeaders } from '../api/client';
import { parseSSE } from '../api/sse';
import { logAction, logResult } from '../observability';

const API_AGENT = '/api/ai';

/**
 * @param {Object} options
 * @param {string} [options.messageType] — 'agent' or 'assistant' (AgentChat uses 'agent', Bubble uses 'assistant')
 * @param {Function} [options.onSessionCreated] — called with conversation_id when backend creates session
 * @param {Function} [options.onStreamComplete] — called after successful stream finishes
 * @param {Function} [options.onError] — called with error message
 */
export function useChatStream({ messageType = 'agent', onSessionCreated, onStreamComplete, onError } = {}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const threadRef = useRef(null);

  // ─── Send message with SSE streaming ──────────────────────
  // 2026-04-20 Phase 2 (audit P1-1): per-request stream IDs. Previously the
  // in-flight assistant bubble carried the global sentinel id `'streaming'`,
  // so a hypothetical overlapping second send would clobber the first's
  // bubble mid-delta. Now each send generates a UUID and filter/upsert logic
  // keys on it. sendMessage also returns the final payload
  // `{ requestId, text, toolChips, provider, aborted, error }` so callers
  // (useChat) can persist the assistant turn by value instead of by array
  // slicing on `messages.length`.
  const sendMessage = useCallback(async (text, {
    conversationId, projectId, dealId, taskId, personId, view, provider,
    // 2026-04-20 Phase 5 (audit P1-4) — scope correlation. The caller
    // freezes its scope array at send start, forwards a monotonic
    // `scope_version` and optional `scope_entries` for server-side
    // archive validation + future stale-scope detection.
    scopeVersion, scopeEntries,
  } = {}) => {
    const msg = (text || '').trim();
    if (!msg || loading) return null;

    const cid = logAction('chat:sendMessage', { length: msg.length });
    const requestId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const loadingId = `loading-${requestId}`;

    setInput('');
    setError(null);
    setMessages(prev => [...prev, { type: 'user', text: msg }]);
    setLoading(true);
    setMessages(prev => [...prev, { type: 'loading', id: loadingId }]);

    const controller = new AbortController();
    abortRef.current = controller;
    // 60s safety timeout — if nothing received from server, give user feedback
    // instead of leaving them staring at typing dots forever.
    const timeoutId = setTimeout(() => {
      if (abortRef.current === controller) {
        controller.abort();
        setError('Agent is taking too long to respond. Please try again.');
      }
    }, 60000);

    try {
      const auth = await getAuthHeaders();

      const body = {
        message: msg,
        session_id: sessionId,
        provider: provider || localStorage.getItem('acn-agent-provider') || 'anthropic',
      };
      if (conversationId) body.conversation_id = conversationId;
      if (projectId) body.project_id = projectId;
      if (dealId) body.deal_id = dealId;
      if (taskId) body.task_id = taskId;
      if (personId) body.person_id = personId;
      if (view) body.view = view;
      if (typeof scopeVersion === 'number') body.scope_version = scopeVersion;
      if (Array.isArray(scopeEntries) && scopeEntries.length) body.scope_entries = scopeEntries;

      const resp = await fetch(API_AGENT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      // Server responded — clear the "no response" safety timeout.
      // Streaming may still take >60s for long replies; that's fine now.
      clearTimeout(timeoutId);

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `Agent error ${resp.status}`);
      }

      // Read SSE stream
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let agentText = '';
      let toolChips = [];
      // TICKET-1499: proposed brain chips the server saved from this turn (Confirm / Discard).
      let brainChips = [];
      let buffer = '';
      let usedProvider = provider || localStorage.getItem('acn-agent-provider') || 'anthropic';

      const updateStreamingMessage = (streamText, chips, extra = {}) => {
        setMessages(prev => {
          const next = prev.filter(m => m.id !== loadingId && m.id !== requestId);
          return [...next, { type: messageType, text: streamText, id: requestId, toolChips: [...chips], brainChips, ...extra }];
        });
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        const completePart = parts.join('\n\n');
        const events = completePart ? parseSSE(completePart + '\n\n', {
          onParseError: (err, raw, meta) => {
            logAction('sse_parse', {
              status: 'error',
              error: err?.message || 'malformed sse data',
              event_type: meta?.eventType,
              raw_preview: raw,
              source: 'chat.stream',
            });
          },
        }) : [];

        for (const evt of events) {
          if (evt.type === 'session') {
            setSessionId(evt.data.id);
            if (evt.data.conversation_id && onSessionCreated) {
              onSessionCreated(evt.data.conversation_id);
            }
          } else if (evt.type === 'text') {
            agentText += evt.data.text;
            updateStreamingMessage(agentText, toolChips);
          } else if (evt.type === 'tool_call') {
            toolChips.push({ name: evt.data.name, status: 'running' });
            updateStreamingMessage(agentText, toolChips);
          } else if (evt.type === 'tool_result') {
            const idx = toolChips.findIndex(c => c.name === evt.data.name && c.status === 'running');
            if (idx >= 0) toolChips[idx] = { ...toolChips[idx], status: 'done' };
            updateStreamingMessage(agentText, toolChips);
          } else if (evt.type === 'brain_chips') {
            brainChips = Array.isArray(evt.data?.chips) ? evt.data.chips : [];
            updateStreamingMessage(agentText, toolChips);
          } else if (evt.type === 'done') {
            usedProvider = evt.data?.provider || usedProvider;
            updateStreamingMessage(agentText, toolChips, { provider: usedProvider });
          } else if (evt.type === 'error') {
            setError(evt.data.message);
            if (onError) onError(evt.data.message);
          }
        }
      }

      // Finalize: ensure the bubble exists with the correct final text/provider,
      // regardless of whether a 'done' event fired.
      setMessages(prev => {
        const next = prev.filter(m => m.id !== loadingId && m.id !== requestId);
        return [...next, {
          type: messageType,
          text: agentText || '(No response)',
          id: requestId,
          toolChips: [...toolChips],
          brainChips,
          provider: usedProvider,
        }];
      });

      logResult('chat:sendMessage', 'success', { correlationId: cid, requestId });
      if (onStreamComplete) onStreamComplete({ requestId, text: agentText, toolChips, provider: usedProvider });

      return { requestId, text: agentText, toolChips, brainChips, provider: usedProvider, aborted: false };

    } catch (err) {
      if (err.name === 'AbortError') {
        setMessages(prev => {
          const streaming = prev.find(m => m.id === requestId);
          const next = prev.filter(m => m.id !== loadingId && m.id !== requestId);
          const stoppedText = (streaming?.text || '') + '\n\n[Stopped]';
          return [...next, { type: messageType, text: stoppedText, id: requestId, toolChips: streaming?.toolChips || [] }];
        });
        return { requestId, text: '', toolChips: [], provider: null, aborted: true };
      }
      console.error('[ChatStream] error:', err.message);
      setError(err.message);
      if (onError) onError(err.message);
      setMessages(prev => {
        const next = prev.filter(m => m.id !== loadingId && m.id !== requestId);
        return [...next, { type: messageType, text: `Error: ${err.message}`, id: requestId }];
      });
      logResult('chat:sendMessage', 'error', { correlationId: cid, requestId, error: err.message });
      return { requestId, text: '', toolChips: [], provider: null, aborted: false, error: err.message };
    } finally {
      clearTimeout(timeoutId);
      abortRef.current = null;
      setLoading(false);
    }
  }, [loading, sessionId, messageType, onSessionCreated, onStreamComplete, onError]);

  // ─── Stop active stream ───────────────────────────────────
  const stopGeneration = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  // ─── Clear messages for new conversation ──────────────────
  const clearMessages = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setError(null);
  }, []);

  // ─── Edit last user message ───────────────────────────────
  const editLastMessage = useCallback(() => {
    if (loading) return;
    let lastUserIdx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].type === 'user') { lastUserIdx = i; break; }
    }
    if (lastUserIdx < 0) return;
    const text = messages[lastUserIdx].text;
    setMessages(prev => prev.slice(0, lastUserIdx));
    setInput(text);
  }, [messages, loading]);

  return {
    messages, setMessages,
    input, setInput,
    loading,
    sessionId, setSessionId,
    error, setError,
    threadRef,
    sendMessage,
    stopGeneration,
    clearMessages,
    editLastMessage,
  };
}
